import {
  APISource,
  Dataset,
  ExportOptions,
  ExportResult,
  ResearchDomainTemplate,
} from '@/lib/types';
import { mockAPISources } from '@/lib/mockData';
import { toast } from 'sonner';

interface UseAppHandlersProps {
  selectedDatasets: Dataset[];
  setSelectedDatasets: (
    value: Dataset[] | ((prev: Dataset[]) => Dataset[])
  ) => void;
  setSelectedSource: (source: APISource | null) => void;
  setActiveTab: (tab: string) => void;
  setCorrelations: (correlations: any[]) => void;
  setPreviewDataset: (dataset: Dataset | null) => void;
  setFilteredSources: (sources: APISource[]) => void;
  setAppliedTemplate: (template: ResearchDomainTemplate | null) => void;
  setActiveTutorial: (tutorial: any) => void;
}

export function useAppHandlers({
  selectedDatasets,
  setSelectedDatasets,
  setSelectedSource,
  setActiveTab,
  setCorrelations,
  setPreviewDataset,
  setFilteredSources,
  setAppliedTemplate,
  setActiveTutorial,
}: UseAppHandlersProps) {
  const handleSourceSelect = (source: APISource) => {
    setSelectedSource(source);
    setActiveTab('datasets');
  };

  const handleDatasetSelect = (dataset: Dataset) => {
    const isSelected = selectedDatasets.some(d => d.id === dataset.id);

    if (isSelected) {
      setSelectedDatasets(prev => prev.filter(d => d.id !== dataset.id));
    } else {
      setSelectedDatasets(prev => [...prev, dataset]);
    }
  };

  const handleAnalyzeCorrelations = () => {
    if (selectedDatasets.length >= 2) {
      // Mock correlation analysis
      const mockCorrelations = [
        {
          dataset1: selectedDatasets[0].name,
          dataset2: selectedDatasets[1].name,
          correlation: 0.85,
          type: 'strong positive',
        },
      ];

      setCorrelations(mockCorrelations);
      setActiveTab('correlations');
      toast.success('Correlation analysis complete!');
    }
  };

  const handleGlobalSearch = (query: string) => {
    // Mock global search - implementation is handled by GlobalSearch component
    // This is a placeholder for potential future global state management
  };

  const handleSuggestionClick = (suggestion: any) => {
    if (suggestion.action === 'analyze') {
      handleAnalyzeCorrelations();
    } else if (suggestion.tab) {
      setActiveTab(suggestion.tab);
    }
  };

  const handleRemoveDataset = (datasetId: string) => {
    setSelectedDatasets(prev => prev.filter(d => d.id !== datasetId));
  };

  const handleDatasetPreview = (dataset: Dataset) => {
    setPreviewDataset(dataset);
  };

  const handleFilterChange = (filtered: APISource[]) => {
    setFilteredSources(filtered);
  };

  const handleTemplateSelect = (template: ResearchDomainTemplate) => {
    setAppliedTemplate(template);
    // Apply template filters
    const filtered = mockAPISources.filter(source =>
      template.keywords.some(
        keyword =>
          source.name.toLowerCase().includes(keyword.toLowerCase()) ||
          source.category.toLowerCase().includes(keyword.toLowerCase())
      )
    );

    setFilteredSources(filtered);
    setActiveTab('discover');
  };

  const handleClearTemplate = () => {
    setAppliedTemplate(null);
    setFilteredSources(mockAPISources);
  };

  const handleTutorialComplete = (tutorial: any) => {
    setActiveTutorial(null);
    toast.success('Tutorial completed!');
  };

  const handleTutorialStart = (tutorial: any) => {
    setActiveTutorial(tutorial);
  };

  const handleBulkCorrelationAnalysis = () => {
    handleAnalyzeCorrelations();
  };

  const handleDataExport = async (
    options: ExportOptions
  ): Promise<ExportResult> => {
    toast.success('Data exported successfully!');

    return {
      format: options.format,
      size: 1200000,
      downloadUrl: '/downloads/export.csv',
      filename: 'export.csv',
      metadata: {
        exportedAt: new Date().toISOString(),
        totalRows: 1000,
        totalFields: 10,
        datasetsIncluded: selectedDatasets.map(d => d.name),
      },
    };
  };

  return {
    handleSourceSelect,
    handleDatasetSelect,
    handleAnalyzeCorrelations,
    handleGlobalSearch,
    handleSuggestionClick,
    handleRemoveDataset,
    handleDatasetPreview,
    handleFilterChange,
    handleTemplateSelect,
    handleClearTemplate,
    handleTutorialComplete,
    handleTutorialStart,
    handleBulkCorrelationAnalysis,
    handleDataExport,
  };
}
